import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import UncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import type { InstantRange, TimeRangeOnDate } from "@thalamos/common";
import { dayjs, UK_TIME_ZONE, unwrapOrThrow } from "@thalamos/common";
import type {
  AddInterviewParticipantInput,
  CreateInterviewInput,
  CreateWorkItemRequest,
  ExtendedThalamosUser,
  FormContextData,
  InterviewParticipant,
  InterviewWithParticipants,
  TeamType,
  UpdateInterviewInput,
} from "@thalamos/aspire-common";

import {
  formatTimeRangeOnDate,
  requestFromInstants,
  requestToInstants,
  unusableRanges,
} from "../RequestedAvailability/requestedAvailability.js";
import { RequestedAvailabilityPicker } from "../RequestedAvailability/RequestedAvailabilityPicker.js";
import { S12DoctorRequestDialog } from "../S12DoctorRequest/S12DoctorRequestDialog.js";
import {
  getDisplayStatus,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getStatusTextColor,
} from "./participantStatusHelpers.js";
import { api } from "~/api.js";
import {
  AddressField,
  Banner,
  BannerList,
  renderErrorToast as defaultRenderErrorToast,
  renderSuccessToast as defaultRenderSuccessToast,
} from "~/components/design-system/index.js";
import {
  NewFormAssigneeDialog,
  type NewResultWithoutAccreditation,
} from "~/components/FormAssignee/NewFormAssigneeDialog.js";
import { config } from "~/config.js";
import { assertOkResponse, isRefusal } from "~/helpers/assertOkResponse.js";
import {
  actions,
  getTemplate,
} from "~/pages/FormProgressPage/helpers/requestWorkItemActions.js";

// ============================================================================
// Props
// ============================================================================

type ConfirmParticipantRequestBody = {
  noteToDoctor?: string | null;
};

type AddressFieldValue = {
  address?: string;
  postalCode?: string;
};

// ============================================================================
// Props
// ============================================================================

/** API functions used by InterviewManagementDialog — injectable for testing */
export interface InterviewManagementDialogApi {
  getForFormContext: (
    formContextId: string,
  ) => Promise<{ data: InterviewWithParticipants[] }>;
  get: (interviewId: string) => Promise<{ data: InterviewWithParticipants }>;
  create: (body: CreateInterviewInput) => Promise<unknown>;
  update: (interviewId: string, body: UpdateInterviewInput) => Promise<unknown>;
  cancel: (interviewId: string) => Promise<unknown>;
  addParticipant: (
    interviewId: string,
    body: AddInterviewParticipantInput,
  ) => Promise<{ data: InterviewWithParticipants }>;
  confirmParticipant: (
    interviewId: string,
    participantId: string,
    body?: ConfirmParticipantRequestBody,
  ) => Promise<unknown>;
  revokeParticipant: (
    interviewId: string,
    participantId: string,
  ) => Promise<unknown>;
  unconfirmParticipant: (
    interviewId: string,
    participantId: string,
  ) => Promise<unknown>;
  createWorkItem: (
    workItemId: string,
    createRequest: CreateWorkItemRequest,
  ) => Promise<unknown>;
  ensureGuestUser: (
    email: string,
    name: string,
  ) => Promise<{ data: { userId: string; teamId: string } }>;
}

/** Default API implementation wiring to the real API client */
const defaultDialogApi: InterviewManagementDialogApi = {
  getForFormContext: (formContextId) =>
    api.interviews.getForFormContext(formContextId),
  get: (interviewId) => api.interviews.get(interviewId),
  create: (body) => api.interviews.create(body).then(assertOkResponse),
  update: (interviewId, body) =>
    api.interviews.update(interviewId, body).then(assertOkResponse),
  cancel: (interviewId) =>
    api.interviews.cancel(interviewId).then(assertOkResponse),
  addParticipant: (interviewId, body) =>
    api.interviews.addParticipant(interviewId, body).then(assertOkResponse),
  confirmParticipant: (interviewId, participantId, body) =>
    api.interviews
      .confirmParticipant(interviewId, participantId, body)
      .then(assertOkResponse),
  revokeParticipant: (interviewId, participantId) =>
    api.interviews
      .revokeParticipant(interviewId, participantId)
      .then(assertOkResponse),
  unconfirmParticipant: (interviewId, participantId) =>
    api.interviews
      .unconfirmParticipant(interviewId, participantId)
      .then(assertOkResponse),
  createWorkItem: (workItemId, createRequest) =>
    api.work.create(workItemId, createRequest).then(assertOkResponse),
  ensureGuestUser: (email, name) =>
    api.users.ensureGuestUser(email, name).then(assertOkResponse),
};

interface InterviewManagementDialogProps {
  formContextId: string;
  formContext: Omit<FormContextData, "patient">;
  user: ExtendedThalamosUser;
  initialView?: "choose-path" | "list";
  onClose: () => void;
  reloadFormContext: () => void;
  /** Whether the AMHP has an active work item on this admission. When false,
   * interview management actions are disabled. */
  hasActiveWorkItem: boolean;
  /** Override navigation action for "I know who to invite" path */
  onInviteDirect?: () => void;
  /** Optional API override for testing / Storybook */
  apiOverride?: InterviewManagementDialogApi;
  /** Optional toast overrides for testing / Storybook */
  renderSuccessToast?: (props: { message: string }) => void;
  renderErrorToast?: (props: { message: string }) => void;
}

// ============================================================================
// Internal Types
// ============================================================================

type DialogView =
  | { type: "choose-path" }
  | { type: "list" }
  | {
      type: "create";
      nextAction?: "find-doctor";
      /** Tracks where create mode was launched from so cancel/back returns to
       * the correct dialog view even when creation continues into doctor
       * search. */
      returnView: "choose-path" | "list";
    }
  | { type: "edit"; interview: InterviewWithParticipants }
  | { type: "edit-details"; interview: InterviewWithParticipants }
  | { type: "invite-direct" }
  | {
      type: "find-doctor";
      interview: InterviewWithParticipants;
    }
  | {
      type: "confirm-details";
      interview: InterviewWithParticipants;
      participantId: string;
    };

// ============================================================================
// Component
// ============================================================================

export function InterviewManagementDialog({
  formContextId,
  formContext,
  user,
  initialView = "choose-path",
  onClose,
  reloadFormContext,
  hasActiveWorkItem,
  onInviteDirect,
  apiOverride,
  renderSuccessToast = defaultRenderSuccessToast,
  renderErrorToast = defaultRenderErrorToast,
}: InterviewManagementDialogProps) {
  const apiClient = apiOverride ?? defaultDialogApi;
  const [view, setView] = useState<DialogView>({ type: initialView });
  const [viewTitleOverride, setViewTitleOverride] = useState<string | null>(
    null,
  );
  const [viewBackActionOverride, setViewBackActionOverride] = useState<
    (() => void) | null
  >(null);
  const [interviews, setInterviews] = useState<
    InterviewWithParticipants[] | null
  >(null);
  const [loading, setLoading] = useState(true);

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getForFormContext(formContextId);
      setInterviews(Array.isArray(response.data) ? response.data : []);
    } catch {
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, formContextId]);

  useEffect(() => {
    void loadInterviews();
  }, [loadInterviews]);

  const handleCreated = useCallback(
    async (nextAction?: "find-doctor") => {
      await loadInterviews();
      reloadFormContext();

      if (nextAction === "find-doctor") {
        // Reload interviews, then auto-select the latest one for search
        const response = await apiClient.getForFormContext(formContextId);
        const active = response.data.filter((i) => i.status !== "cancelled");
        const latestActive = active.at(-1);
        if (latestActive !== undefined) {
          setView({
            type: "find-doctor",
            interview: latestActive,
          });
          return;
        }
      }

      setView({ type: "list" });
    },
    [apiClient, formContextId, loadInterviews, reloadFormContext],
  );

  const handleUpdated = useCallback(() => {
    void loadInterviews();
    reloadFormContext();
    setView({ type: "list" });
  }, [loadInterviews, reloadFormContext]);

  const activeInterviews = (interviews ?? []).filter(
    (i) => i.status !== "cancelled",
  );

  const getDialogTitle = (): string => {
    if (viewTitleOverride) {
      return viewTitleOverride;
    }

    switch (view.type) {
      case "choose-path":
        return "Invite Doctor for MHA Assessment";
      case "list":
        return "Manage MHA Assessments";
      case "create":
        return view.nextAction === "find-doctor"
          ? "Find Doctor for Independent MHA Assessment"
          : "Create Assessment";
      case "edit":
        return "Assessment Details";
      case "edit-details":
        return "Edit Assessment Details";
      case "find-doctor":
        return "Find a Doctor Nearby";
      case "invite-direct":
        return "Invite Doctor";
      case "confirm-details":
        return "Confirm Assessment Details";
    }
  };

  const getBackAction = (): (() => void) | null => {
    if (viewBackActionOverride) {
      return viewBackActionOverride;
    }

    switch (view.type) {
      case "choose-path":
        return null;
      case "list":
        return initialView === "list"
          ? null
          : () => {
              setView({ type: "choose-path" });
            };
      case "create":
        return () => {
          setView({ type: view.returnView });
        };
      case "edit":
        return () => {
          setView({ type: "list" });
        };
      case "edit-details":
        return () => {
          setView({ type: "list" });
        };
      case "find-doctor":
        return () => {
          setView({ type: "choose-path" });
        };
      case "invite-direct":
        return () => {
          setView({ type: "choose-path" });
        };
      case "confirm-details":
        return () => {
          setView({ type: "edit", interview: view.interview });
        };
    }
  };

  const backAction = getBackAction();

  const handleChildShellStateChange = useCallback(
    (state: { title?: string; backAction?: (() => void) | null }) => {
      setViewTitleOverride(state.title ?? null);
      // Wrap in an updater arrow so React stores the function reference
      // instead of calling it as a state updater.
      setViewBackActionOverride(() => state.backAction ?? null);
    },
    [],
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // When the "find-doctor" view is active, render the full S12DoctorRequestDialog
  // in place of the interview management dialog — it has its own Dialog chrome.
  if (view.type === "find-doctor") {
    return (
      <S12DoctorRequestDialog
        interview={view.interview}
        user={user}
        onClose={() => {
          reloadFormContext();
          onClose();
        }}
        reloadFormContext={reloadFormContext}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      data-testid="interview-management-dialog"
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {backAction && (
              <Button
                size="small"
                onClick={backAction}
                aria-label="Back"
                sx={{ minWidth: 0 }}
              >
                ←
              </Button>
            )}
            <Typography variant="h6">{getDialogTitle()}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && view.type === "choose-path" && (
          <ChoosePathView
            onInviteDirect={
              onInviteDirect ??
              (() => {
                setView({ type: "invite-direct" });
              })
            }
            onFindDoctor={() => {
              setView({
                type: "create",
                nextAction: "find-doctor",
                returnView: "choose-path",
              });
            }}
            onManageInterviews={() => {
              setView({ type: "list" });
            }}
            activeInterviewCount={activeInterviews.length}
          />
        )}

        {!loading && view.type === "invite-direct" && (
          <InviteDirectView
            formContext={formContext}
            formContextId={formContextId}
            user={user}
            activeInterviews={activeInterviews}
            reloadFormContext={reloadFormContext}
            onComplete={() => {
              void loadInterviews();
              onClose();
            }}
            onShellStateChange={handleChildShellStateChange}
            apiClient={apiClient}
            renderSuccessToast={renderSuccessToast}
            renderErrorToast={renderErrorToast}
          />
        )}

        {!loading && view.type === "list" && (
          <InterviewListView
            interviews={interviews ?? []}
            onEditDetails={(interview) => {
              setView({ type: "edit-details", interview });
            }}
            onCancel={async (interviewId) => {
              try {
                await apiClient.cancel(interviewId);
                renderSuccessToast({ message: "Assessment cancelled" });
                void loadInterviews();
                reloadFormContext();
              } catch (error) {
                renderErrorToast({
                  message:
                    error instanceof Error
                      ? error.message
                      : "Something went wrong. Please try again.",
                });
              }
            }}
            onFindDoctor={(interview) => {
              setView({ type: "find-doctor", interview });
            }}
            disabled={!hasActiveWorkItem}
          />
        )}

        {!loading && view.type === "create" && (
          <InterviewFormView
            formContextId={formContextId}
            onSave={() => {
              void handleCreated(view.nextAction);
            }}
            onCancel={() => {
              setView({ type: view.returnView });
            }}
            onShellStateChange={handleChildShellStateChange}
            apiClient={apiClient}
            renderSuccessToast={renderSuccessToast}
            renderErrorToast={renderErrorToast}
          />
        )}

        {!loading && view.type === "edit" && (
          <InterviewDetailView
            interview={view.interview}
            onUpdated={handleUpdated}
            onBack={() => {
              setView({ type: "list" });
            }}
            onConfirmDetails={(participantId) => {
              setView({
                type: "confirm-details",
                interview: view.interview,
                participantId,
              });
            }}
            apiClient={apiClient}
            renderSuccessToast={renderSuccessToast}
            renderErrorToast={renderErrorToast}
          />
        )}

        {!loading && view.type === "edit-details" && (
          <InterviewFormView
            formContextId={formContextId}
            mode="edit"
            initialValues={{
              address: view.interview.address,
              postalCode: view.interview.postalCode,
              locationUnknown: view.interview.locationUnknown,
              timeRanges: view.interview.timeRanges,
            }}
            onSave={async (updateBody) => {
              await apiClient.update(view.interview.id, updateBody);
              renderSuccessToast({
                message: "Assessment details updated",
              });
              await loadInterviews();
              reloadFormContext();
              setView({ type: "list" });
            }}
            onCancel={() => {
              setView({ type: "list" });
            }}
            onShellStateChange={handleChildShellStateChange}
            apiClient={apiClient}
            renderSuccessToast={renderSuccessToast}
            renderErrorToast={renderErrorToast}
          />
        )}

        {!loading && view.type === "confirm-details" && (
          <InterviewFormView
            formContextId={formContextId}
            mode="confirm"
            initialValues={{
              address: view.interview.address,
              postalCode: view.interview.postalCode,
              locationUnknown: view.interview.locationUnknown,
              timeRanges: view.interview.timeRanges,
            }}
            onSave={async (updateBody, extra) => {
              await apiClient.update(view.interview.id, updateBody);
              await apiClient.confirmParticipant(
                view.interview.id,
                view.participantId,
                { noteToDoctor: extra?.noteToDoctor ?? null },
              );
              renderSuccessToast({
                message: "Assessment details updated and doctor confirmed",
              });
              await loadInterviews();
              reloadFormContext();
              setView({ type: "list" });
            }}
            onCancel={() => {
              setView({ type: "edit", interview: view.interview });
            }}
            onShellStateChange={handleChildShellStateChange}
            apiClient={apiClient}
            renderSuccessToast={renderSuccessToast}
            renderErrorToast={renderErrorToast}
          />
        )}
      </DialogContent>
      {view.type === "choose-path" && (
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
      {view.type === "list" && (
        <DialogActions
          sx={{
            justifyContent: "space-between",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            px: 3,
            py: 2,
          }}
        >
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setView({
                type: "create",
                nextAction: "find-doctor",
                returnView: "list",
              });
            }}
            disabled={!hasActiveWorkItem}
            sx={{
              color: "white",
              "& .MuiButton-startIcon": { color: "white" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Organise New MHA Assessment
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

// ============================================================================
// Choose Path View — the two-option landing screen
// ============================================================================

function ChoosePathView({
  onInviteDirect,
  onFindDoctor,
  onManageInterviews,
  activeInterviewCount,
}: {
  onInviteDirect: () => void;
  onFindDoctor: () => void;
  onManageInterviews: () => void;
  activeInterviewCount: number;
}) {
  return (
    <Box>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          textAlign: "center",
          mb: 3,
        }}
      >
        How would you like to find a doctor for this assessment?
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyContent: "center",
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        {/* Path 1: I know who I want */}
        <Card
          variant="outlined"
          sx={{
            flex: "1 1 280px",
            maxWidth: 360,
            transition: "border-color 0.2s, box-shadow 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              boxShadow: 2,
            },
          }}
        >
          <CardActionArea
            onClick={onInviteDirect}
            data-testid="path-invite-direct"
            sx={{ height: "100%" }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                py: 4,
                px: 3,
              }}
            >
              <PersonAddIcon
                sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                I know who to invite
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Send an invitation directly to a specific doctor or team you
                already have in mind.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Path 2: Help me find a doctor */}
        <Card
          variant="outlined"
          sx={{
            flex: "1 1 280px",
            maxWidth: 360,
            transition: "border-color 0.2s, box-shadow 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              boxShadow: 2,
            },
          }}
        >
          <CardActionArea
            onClick={onFindDoctor}
            data-testid="path-find-doctor"
            sx={{ height: "100%" }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                py: 4,
                px: 3,
              }}
            >
              <SearchIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Help me find a doctor
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Search for available Section 12 doctors near the assessment
                location based on their availability and specialisms.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
      {/* Manage existing interviews link */}
      {activeInterviewCount > 0 && (
        <Box
          sx={{
            textAlign: "center",
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={onManageInterviews}
            data-testid="manage-interviews-link"
          >
            Manage existing assessments ({activeInterviewCount})
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Invite Direct View — search, select recipient, pick interview, send
// ============================================================================

type InviteRecipient = {
  entityType?: string;
  teamId?: string;
  assignedUserId?: string;
  email?: string;
  name?: string;
  teamName?: string;
  type?: string;
  guestInviteAccreditation?: string;
};

type InviteDirectStep =
  | { type: "search" }
  | {
      type: "create-interview";
      recipient: InviteRecipient;
    };

function InviteDirectView({
  formContext,
  formContextId,
  user,
  activeInterviews,
  reloadFormContext,
  onComplete,
  onShellStateChange,
  apiClient,
  renderSuccessToast,
  renderErrorToast,
}: {
  formContext: Omit<FormContextData, "patient">;
  formContextId: string;
  user: ExtendedThalamosUser;
  activeInterviews: InterviewWithParticipants[];
  reloadFormContext: () => void;
  onComplete: () => void;
  onShellStateChange?: (state: {
    title?: string;
    backAction?: (() => void) | null;
  }) => void;
  apiClient: InterviewManagementDialogApi;
  renderSuccessToast: (props: { message: string }) => void;
  renderErrorToast: (props: { message: string }) => void;
}) {
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<
    NewResultWithoutAccreditation[]
  >([]);
  const [step, setStep] = useState<InviteDirectStep>({ type: "search" });
  const [selectedRecipient, setSelectedRecipient] =
    useState<InviteRecipient | null>(null);
  const [linkChoice, setLinkChoice] = useState<
    | { type: "none" }
    | { type: "assessment"; interviewId: string }
    | { type: "no-link" }
  >({ type: "none" });
  const [submitting, setSubmitting] = useState(false);
  // Local copy of interviews so we can refresh after creating a new one
  // without the parent's loadInterviews unmounting this component.
  const [localInterviews, setLocalInterviews] =
    useState<InterviewWithParticipants[]>(activeInterviews);

  const formDetails = getTemplate(
    formContext as Omit<FormContextData, "patient">,
  );
  const availableActions = actions(
    formContext as Omit<FormContextData, "patient">,
    user,
  );

  // Interviews without a confirmed or pending-confirmed doctor
  const unconfirmedInterviews = localInterviews.filter(
    (i) =>
      !i.participants.some(
        (p) => p.status === "confirmed" || p.status === "pending-confirmed",
      ),
  );

  const isOptionDisabled = useCallback(
    (option: NewResultWithoutAccreditation) => {
      // Teams: only ward, independent, and guest teams can receive doctor
      // invites. Use an allow-list so any new team type fails closed.
      if (option.entityType === "team") {
        const ALLOWED_TEAM_TYPES = new Set<TeamType>([
          "ward",
          "independent",
          "guest",
        ]);
        if (!ALLOWED_TEAM_TYPES.has(option.teamType as TeamType)) {
          return {
            disabled: true,
            reason: "Only ward, independent, and guest teams can be invited",
          };
        }
        return { disabled: false };
      }

      // Individual users must have a GMC number (i.e. be a registered doctor)
      if (!option.gmcNumber) {
        return {
          disabled: true,
          reason: "This user does not have a GMC number on record",
        };
      }

      return { disabled: false };
    },
    [],
  );

  const handleSendInvite = useCallback(
    async (recipient: InviteRecipient, interviewId: string | null) => {
      setSubmitting(true);
      try {
        const isGuest = recipient.type === "guest";

        // For guests linked to an interview, ensure the guest user exists
        // first so we have a userId to add as a participant.
        let effectiveUserId = recipient.assignedUserId;
        let effectiveTeamId = recipient.teamId;

        if (isGuest && interviewId && !effectiveUserId) {
          if (!recipient.email || !recipient.name) {
            renderErrorToast({
              message:
                "Guest participants require both an email address and a name.",
            });
            setSubmitting(false);
            return;
          }
          const ensureResult = await apiClient.ensureGuestUser(
            recipient.email,
            recipient.name,
          );
          effectiveUserId = ensureResult.data.userId;
          effectiveTeamId = ensureResult.data.teamId;
        }

        if (interviewId && effectiveUserId) {
          // Linked to an interview → add as pending-confirmed participant.
          // No separate confirm call needed — addParticipant already sets
          // the status to pending-confirmed.
          await apiClient.addParticipant(interviewId, {
            userId: effectiveUserId,
            teamId: effectiveTeamId,
            status: "pending-confirmed",
            contactMethod: ["email"],
          });
        } else {
          // No interview link → create a pending work item only
          const workItemId = v4();
          const createRequest = isGuest
            ? {
                type: "guest" as const,
                email: recipient.email ?? "",
                name: recipient.name ?? "",
                formContextId,
                action: { type: "medical-recommendation" as const },
              }
            : {
                type: "full-user" as const,
                teamId: recipient.teamId ?? "",
                assignedUserId: recipient.assignedUserId,
                formContextId,
                action: { type: "medical-recommendation" as const },
              };

          await apiClient.createWorkItem(workItemId, createRequest);
        }

        renderSuccessToast({
          message: `Invitation sent to ${recipient.name ?? recipient.email}`,
        });
        reloadFormContext();
        onComplete();
      } catch (error) {
        if (isRefusal(error)) {
          renderErrorToast({ message: error.message });
        } else {
          console.error("Failed to send interview invitation", error);
          renderErrorToast({
            message: "Failed to send invitation. Please try again.",
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      apiClient,
      formContextId,
      onComplete,
      reloadFormContext,
      renderErrorToast,
      renderSuccessToast,
    ],
  );

  const handleBackToInviteSearch = useCallback(() => {
    if (step.type !== "create-interview") {
      return;
    }

    setSelectedRecipient(step.recipient);
    setStep({ type: "search" });
  }, [step]);

  // The nested form turns pages of its own; while it has one to go back to,
  // the shell's back arrow serves that before it serves the return to search.
  const [formBackAction, setFormBackAction] = useState<(() => void) | null>(
    null,
  );
  const handleFormShellStateChange = useCallback(
    (state: { backAction: (() => void) | null }) => {
      // Updater arrow, as in handleChildShellStateChange: a bare function
      // would run as a state updater.
      setFormBackAction(() => state.backAction);
    },
    [],
  );

  useEffect(() => {
    if (!onShellStateChange) {
      return;
    }

    if (step.type === "create-interview") {
      onShellStateChange({
        title: "Create Assessment",
        backAction: formBackAction ?? handleBackToInviteSearch,
      });
    } else {
      onShellStateChange({
        title: undefined,
        backAction: null,
      });
    }

    return () => {
      onShellStateChange({
        title: undefined,
        backAction: null,
      });
    };
  }, [formBackAction, handleBackToInviteSearch, onShellStateChange, step]);

  // Step: Create interview (inline InterviewFormView)
  if (step.type === "create-interview") {
    return (
      <InterviewFormView
        formContextId={formContextId}
        onSave={async () => {
          const response = await apiClient.getForFormContext(formContextId);
          const active = (
            Array.isArray(response.data) ? response.data : []
          ).filter((i) => i.status !== "cancelled");
          setLocalInterviews(active);

          // Auto-select the newest assessment (last created)
          const newest = active.at(-1);
          if (newest) {
            setLinkChoice({ type: "assessment", interviewId: newest.id });
          }

          setSelectedRecipient(step.recipient);
          setStep({ type: "search" });
        }}
        onCancel={() => {
          setSelectedRecipient(step.recipient);
          setStep({ type: "search" });
        }}
        onShellStateChange={handleFormShellStateChange}
        apiClient={apiClient}
      />
    );
  }

  // Once a recipient is selected the search form hides and the user
  // picks an assessment or sends without one.
  const recipientLabel = selectedRecipient
    ? (selectedRecipient.name ?? selectedRecipient.email)
    : null;

  const isWardTeam =
    selectedRecipient?.entityType === "team" &&
    !selectedRecipient?.assignedUserId;

  return (
    <Box>
      {!selectedRecipient ? (
        <>
          <Box sx={{ mb: 2 }}>
            <Banner
              bannerType={BannerList.INFO}
              title="Search for a doctor to invite to this assessment."
            />
          </Box>
          <NewFormAssigneeDialog
            singleSelectMode
            onRecipientSelected={(values) => {
              setSelectedRecipient(values);
              // Reset link choice — user must actively choose
              setLinkChoice({ type: "none" });
            }}
            isOptionDisabled={isOptionDisabled}
            formId={undefined}
            formContext={formContext}
            reloadFormContext={reloadFormContext}
            availableActions={availableActions}
            searchAssignmentOptions={async (fd, searchValue) => {
              const response = await api.formTemplates.searchAssignmentOptions({
                id: fd.template.id,
                version: fd.template.version,
                query: searchValue,
                part: fd.part,
              });
              return response;
            }}
            formDetails={formDetails}
            userId={user.id}
            userEmail={user.email}
            userTeamId={user.sessionContext?.teamId}
            userDataControllerId={user.sessionContext?.dataControllerId}
            userDataControllerName={user.sessionContext?.dataControllerName}
            dataControllerWarningEnabled={
              user.sessionContext?.teamType !== "guest" &&
              user.sessionContext?.teamType !== "soad" &&
              user.sessionContext?.teamType !== "independent" &&
              (user.sessionOrganisationConfiguration
                ?.dataControllerWarningEnabled ??
                false)
            }
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            onSave={async () => {}}
            navigate={navigate}
          />
        </>
      ) : (
        <Box>
          {/* Collapsed recipient summary */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "action.hover",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonAddIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                Inviting:{" "}
                <strong>
                  {recipientLabel}
                  {selectedRecipient.teamName &&
                    ` (${selectedRecipient.teamName})`}
                </strong>
                {isWardTeam && (
                  <Chip
                    label="Ward team"
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                )}
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={() => setSelectedRecipient(null)}
              disabled={submitting}
            >
              Change
            </Button>
          </Paper>

          {/* Assessment picker */}
          {isWardTeam ? (
            <Box>
              <Banner
                bannerType={BannerList.INFO}
                title="Ward team invitations cannot be linked to a specific assessment. Invite an individual doctor instead to link them to an assessment with time and location details."
              />
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Link to an assessment
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                Recommended — shares the time and location with the doctor so
                they can confirm availability.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  ...(linkChoice.type === "no-link" && {
                    opacity: 0.4,
                    pointerEvents: "none",
                  }),
                }}
              >
                {unconfirmedInterviews.map((interview) => {
                  const isSelected =
                    linkChoice.type === "assessment" &&
                    linkChoice.interviewId === interview.id;
                  return (
                    <Card
                      key={interview.id}
                      variant="outlined"
                      sx={{
                        borderColor: isSelected ? "primary.main" : undefined,
                        borderWidth: isSelected ? 2 : 1,
                      }}
                    >
                      <CardActionArea
                        disabled={submitting}
                        onClick={() =>
                          setLinkChoice({
                            type: "assessment",
                            interviewId: interview.id,
                          })
                        }
                        data-testid={`select-interview-${interview.id}`}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              {interview.address && (
                                <Typography variant="body2">
                                  <LocationOnIcon
                                    sx={{
                                      fontSize: 16,
                                      verticalAlign: "text-bottom",
                                      mr: 0.5,
                                    }}
                                  />
                                  {interview.address}
                                  {interview.postalCode
                                    ? `, ${interview.postalCode}`
                                    : ""}
                                </Typography>
                              )}
                              {interview.timeRanges.length > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary" }}
                                >
                                  {interview.timeRanges
                                    .map(
                                      (tr) =>
                                        `${dayjs(tr.start).format("DD MMM HH:mm")} – ${dayjs(tr.end).format("HH:mm")}`,
                                    )
                                    .join(", ")}
                                </Typography>
                              )}
                            </Box>
                            {isSelected ? (
                              <RadioButtonCheckedIcon color="primary" />
                            ) : (
                              <UncheckedIcon color="action" />
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}

                <CardActionArea
                  disabled={submitting}
                  onClick={() =>
                    setStep({
                      type: "create-interview",
                      recipient: selectedRecipient,
                    })
                  }
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    py: 1.5,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                  data-testid="create-new-assessment-button"
                >
                  <AddIcon fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {unconfirmedInterviews.length === 0
                      ? "Create an assessment"
                      : "Create another assessment"}
                  </Typography>
                </CardActionArea>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={linkChoice.type === "no-link"}
                      onChange={(e) =>
                        setLinkChoice(
                          e.target.checked
                            ? { type: "no-link" }
                            : { type: "none" },
                        )
                      }
                      disabled={submitting}
                      data-testid="send-without-assessment-checkbox"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Send without linking to an assessment
                    </Typography>
                  }
                />
              </Box>
            </>
          )}

          {/* Send invitation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              disabled={
                submitting || (!isWardTeam && linkChoice.type === "none")
              }
              onClick={() =>
                handleSendInvite(
                  selectedRecipient,
                  linkChoice.type === "assessment"
                    ? linkChoice.interviewId
                    : null,
                )
              }
              data-testid="send-invitation-button"
            >
              Send invitation
            </Button>
          </Box>

          {submitting && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// List View
// ============================================================================

function InterviewListView({
  interviews,
  onEditDetails,
  onCancel,
  onFindDoctor,
  disabled,
}: {
  interviews: InterviewWithParticipants[];
  onEditDetails: (interview: InterviewWithParticipants) => void;
  onCancel: (interviewId: string) => Promise<void>;
  onFindDoctor: (interview: InterviewWithParticipants) => void;
  disabled?: boolean;
}) {
  const activeInterviews = interviews
    .filter((i) => i.status !== "cancelled")
    .sort((a, b) => a.created.localeCompare(b.created));
  const cancelledInterviews = interviews
    .filter((i) => i.status === "cancelled")
    .sort((a, b) => a.created.localeCompare(b.created));

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        View and manage your MHA assessments. Use the action menu on each
        assessment to edit details, continue with doctor search, or delete.
      </Typography>
      {activeInterviews.length === 0 && cancelledInterviews.length === 0 && (
        <Typography
          sx={{
            color: "text.secondary",
            textAlign: "center",
            py: 4,
          }}
        >
          No assessments yet. Create one to invite doctors to an assessment.
        </Typography>
      )}
      {activeInterviews.map((interview, index) => {
        const interviewHasConfirmedDoctor = interview.participants.some(
          (p) => p.status === "confirmed" || p.status === "pending-confirmed",
        );
        return (
          <InterviewCard
            key={interview.id}
            interview={interview}
            interviewLabel={`MHA Assessment ${String(index + 1)}`}
            onEditDetails={() => {
              onEditDetails(interview);
            }}
            onCancel={() => onCancel(interview.id)}
            onFindDoctor={
              interviewHasConfirmedDoctor
                ? undefined
                : () => {
                    onFindDoctor(interview);
                  }
            }
            disabled={disabled}
          />
        );
      })}
      {cancelledInterviews.length > 0 && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            Cancelled Assessments
          </Typography>
          {cancelledInterviews.map((interview, index) => (
            <InterviewCard
              key={interview.id}
              interview={interview}
              interviewLabel={`MHA Assessment ${String(activeInterviews.length + index + 1)}`}
              disabled
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Interview Card
// ============================================================================

function InterviewCard({
  interview,
  interviewLabel,
  onEditDetails,
  onCancel,
  onFindDoctor,
  disabled,
}: {
  interview: InterviewWithParticipants;
  interviewLabel: string;
  onEditDetails?: () => void;
  onCancel?: () => Promise<void>;
  onFindDoctor?: () => void;
  disabled?: boolean;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleCancel = async () => {
    if (!onCancel) return;
    setAnchorEl(null);
    setCancelling(true);
    try {
      await onCancel();
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2, opacity: disabled ? 0.6 : 1 }}
      data-testid="interview-card"
    >
      <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {interviewLabel}
              </Typography>
              {interview.status === "cancelled" && (
                <Chip label="Cancelled" size="small" color="error" />
              )}
            </Box>

            {interview.address && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {interview.address}
                {interview.postalCode && `, ${interview.postalCode}`}
              </Typography>
            )}

            {interview.locationUnknown && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Location to be confirmed
              </Typography>
            )}

            {interview.timeRanges.length > 0 && (
              <Box
                sx={{
                  mt: 0.5,
                }}
              >
                {interview.timeRanges.map((range, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {new Date(range.start).toLocaleString()} –{" "}
                    {new Date(range.end).toLocaleString()}
                  </Typography>
                ))}
              </Box>
            )}

            <Box
              sx={{
                mt: 1,
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
              }}
            >
              {interview.participants.map((p) => {
                const displayStatus = getDisplayStatus(p);
                const displayName = p.userName || p.userEmail || "Unknown";
                const labelWithTeam =
                  p.teamType !== "independent" && p.teamName
                    ? `${displayName} (${p.teamName})`
                    : displayName;
                const tooltipLines = [
                  labelWithTeam,
                  getStatusLabel(displayStatus),
                  ...(p.status === "rejected" && p.rejectionReason
                    ? [p.rejectionReason]
                    : []),
                ];

                return (
                  <Tooltip key={p.id} title={tooltipLines.join(" – ")} arrow>
                    <Chip
                      icon={getStatusIcon(displayStatus)}
                      label={labelWithTeam}
                      size="small"
                      color={getStatusColor(displayStatus)}
                      variant="filled"
                      sx={{
                        maxWidth: 180,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: getStatusTextColor(displayStatus),
                        },
                        "& .MuiChip-icon": {
                          color: `${getStatusTextColor(displayStatus)} !important`,
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          {!disabled && (
            <Box sx={{ ml: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                }}
                endIcon={<MoreVertIcon />}
                disabled={cancelling}
                aria-label="Actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-controls={menuOpen ? "interview-action-menu" : undefined}
                data-testid="interview-action-button"
              >
                Action
              </Button>
              <Menu
                id="interview-action-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => {
                  setAnchorEl(null);
                }}
              >
                {onFindDoctor && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onFindDoctor();
                    }}
                  >
                    Continue
                  </MenuItem>
                )}
                {onEditDetails && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onEditDetails();
                    }}
                  >
                    Edit
                  </MenuItem>
                )}
                {onCancel && (
                  <MenuItem
                    onClick={() => {
                      void handleCancel();
                    }}
                    disabled={cancelling}
                    sx={{ color: "error.main" }}
                  >
                    {cancelling ? (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    ) : null}
                    Delete
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Create / Update Interview Form
// ============================================================================

/**
 * The form is two pages, so the calendar has the dialog's height to itself:
 * the location, then the time.
 */
type InterviewFormPage = "location" | "time";

/** The location as the time page shows it, with the way back to changing it. */
function AssessmentLocationSummary({
  address,
  postalCode,
  locationUnknown,
  onEdit,
}: {
  address: string | undefined;
  postalCode: string | undefined;
  locationUnknown: boolean;
  onEdit: () => void;
}) {
  const description = locationUnknown
    ? "Location not yet known"
    : postalCode
      ? [address, postalCode].filter(Boolean).join(", ")
      : "No location set";

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
      }}
      data-testid="assessment-location-summary"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <LocationOnIcon fontSize="small" color="primary" />
          Assessment Location
        </Typography>
        <IconButton
          size="small"
          onClick={onEdit}
          aria-label="Edit assessment location"
          data-testid="edit-assessment-location-button"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {description}
      </Typography>
    </Box>
  );
}

export function InterviewFormView({
  formContextId,
  onSave,
  onCancel,
  onShellStateChange,
  apiClient,
  mode = "create",
  initialValues,
  renderSuccessToast = defaultRenderSuccessToast,
  renderErrorToast = defaultRenderErrorToast,
}: {
  formContextId: string;
  onSave: (
    updateBody: UpdateInterviewInput,
    extra?: { noteToDoctor?: string | null },
  ) => void | Promise<void>;
  onCancel: () => void;
  /**
   * Lets the dialog's back arrow retrace a page turn made inside the form.
   * `null` hands the arrow back to whatever the shell would otherwise do.
   */
  onShellStateChange?: (state: { backAction: (() => void) | null }) => void;
  apiClient: InterviewManagementDialogApi;
  /** "create" = new interview, "edit" = update the requested time ranges, "confirm" = set the agreed time before confirming a doctor */
  mode?: "create" | "edit" | "confirm";
  /** Pre-populate location & time fields (used in edit / confirm mode) */
  initialValues?: {
    address?: string | null;
    postalCode?: string | null;
    locationUnknown?: boolean;
    timeRanges?: InstantRange[];
  };
  renderSuccessToast?: (props: { message: string }) => void;
  renderErrorToast?: (props: { message: string }) => void;
}) {
  const [addressValue, setAddressValue] = useState<AddressFieldValue>(
    initialValues?.address
      ? {
          address: initialValues.address,
          postalCode: initialValues.postalCode ?? "",
        }
      : {},
  );
  const [locationUnknown, setLocationUnknown] = useState(
    initialValues?.locationUnknown ?? false,
  );
  // A time agreed to the second has no faithful whole-minute range, so seeding
  // fails rather than moving it, and the AMHP is warned instead.
  const seededRequest = useMemo(
    () => requestFromInstants(initialValues?.timeRanges ?? []),
    [initialValues?.timeRanges],
  );
  const [requestedRanges, setRequestedRanges] = useState<TimeRangeOnDate[]>(
    () => seededRequest.map((request) => request.ranges).unwrapOr([]),
  );
  const widenedRanges = useMemo(
    () => seededRequest.map((request) => request.widened).unwrapOr([]),
    [seededRequest],
  );
  const [saving, setSaving] = useState(false);
  const [noteToDoctor, setNoteToDoctor] = useState("");

  // Create has no location yet, so it starts there. Edit and confirm already
  // have one, so they open on the time and the location's edit control leads
  // back.
  const openingPage: InterviewFormPage =
    mode === "create" ? "location" : "time";
  const [page, setPage] = useState<InterviewFormPage>(openingPage);

  useEffect(() => {
    if (!onShellStateChange) {
      return;
    }
    onShellStateChange({
      backAction:
        page === openingPage
          ? null
          : () => {
              setPage(openingPage);
            },
    });
    return () => {
      onShellStateChange({ backAction: null });
    };
  }, [onShellStateChange, page, openingPage]);

  const hasValidLocation = locationUnknown || !!addressValue.postalCode?.trim();

  // UK wall clock, as every `now` given to `RequestedAvailabilityPicker` must be.
  const now = useMemo(() => dayjs().tz(UK_TIME_ZONE), []);
  const unusable = useMemo(
    () => unusableRanges(requestedRanges),
    [requestedRanges],
  );
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const previousRangeCountRef = useRef<number>(requestedRanges.length);

  useEffect(() => {
    // Edit and confirm open with times already chosen, so a scroll would jump
    // for no reason.
    if (mode !== "create") {
      return;
    }

    const previousCount = previousRangeCountRef.current;
    const currentCount = requestedRanges.length;

    // Only the first range earns a scroll; every later edit would drag the page.
    if (previousCount === 0 && currentCount > 0 && actionButtonsRef.current) {
      actionButtonsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }

    previousRangeCountRef.current = currentCount;
  }, [mode, requestedRanges]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const timeRanges: InstantRange[] = unwrapOrThrow(
        requestToInstants(requestedRanges),
      );

      const updateBody: UpdateInterviewInput = {
        address: locationUnknown
          ? null
          : (addressValue.address as string) || null,
        postalCode: locationUnknown
          ? null
          : (addressValue.postalCode as string) || null,
        locationUnknown,
        timeRanges,
      };

      if (mode === "create") {
        const body: CreateInterviewInput = {
          formContextId,
          ...updateBody,
        };
        await apiClient.create(body);
        renderSuccessToast({ message: "Assessment created" });
      }

      await onSave(
        updateBody,
        mode === "confirm" ? { noteToDoctor: noteToDoctor || null } : undefined,
      );
    } catch (error) {
      renderErrorToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (page === "location") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          mb: 2,
        }}
        data-testid="interview-form-location-page"
      >
        <LocationOnIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Where is the assessment taking place?
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 3,
            maxWidth: 440,
          }}
        >
          Setting the assessment location helps calculate travel times for
          available doctors.
        </Typography>

        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {!locationUnknown && (
            <AddressField
              name="assessmentLocation"
              label="Assessment Location"
              addressLabel="Address"
              postalLabel="Postcode"
              googleMapsApiKey={config.googleMapsApiKey}
              addressCheckBoxLabel=""
              hideConfirmation
              value={{
                address: addressValue.address ?? "",
                postalCode: addressValue.postalCode ?? "",
              }}
              onChange={(values) => {
                if (values) {
                  setAddressValue(
                    typeof values === "object" && values !== null
                      ? (values as AddressFieldValue)
                      : {},
                  );
                }
              }}
              testId="assessment-location"
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={locationUnknown}
                onChange={(e) => {
                  setLocationUnknown(e.target.checked);
                }}
                size="small"
                data-testid="assessment-location-unknown-toggle"
              />
            }
            label={
              <Typography variant="body2">Location not yet known</Typography>
            }
            sx={{ mt: 1, alignSelf: "flex-start" }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setPage("time");
            }}
            disabled={!hasValidLocation}
            sx={{ mt: 3 }}
            data-testid="confirm-location-button"
          >
            Continue
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid="interview-form-time-page">
      <AssessmentLocationSummary
        address={addressValue.address}
        postalCode={addressValue.postalCode}
        locationUnknown={locationUnknown}
        onEdit={() => {
          setPage("location");
        }}
      />
      {/* Time section */}
      {mode === "confirm" ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            When is the assessment taking place?
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Paint the time agreed with the doctor. Their calendar invitation
            will show these times.
          </Typography>
        </>
      ) : (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          When should the assessment take place?
        </Typography>
      )}

      {seededRequest.isErr() && (
        <Box sx={{ mb: 2 }}>
          <Banner
            bannerType={BannerList.WARNING}
            title="The times already saved for this assessment could not be shown on the calendar. Choose them again below."
          />
        </Box>
      )}

      {widenedRanges.length > 0 && (
        <Box sx={{ mb: 2 }} data-testid="saved-times-widened-banner">
          <Banner
            bannerType={BannerList.WARNING}
            title="Some saved times do not sit on the quarter hour, so the calendar shows them widened to it."
            body={widenedRanges.map(formatTimeRangeOnDate)}
          />
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <RequestedAvailabilityPicker
          value={requestedRanges}
          onChange={setRequestedRanges}
          now={now}
        />
      </Box>
      {mode === "confirm" && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Note to doctor (optional)
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            Add any additional information the doctor should know about this
            assessment.
          </Typography>
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            value={noteToDoctor}
            onChange={(e) => {
              setNoteToDoctor(e.target.value);
            }}
            slotProps={{
              htmlInput: {
                maxLength: 1000,
                "aria-label": "Note to doctor (optional)",
              },
            }}
            placeholder="E.g. parking instructions, access details, patient considerations…"
            helperText={`${String(noteToDoctor.length)}/1000`}
            size="small"
            data-testid="note-to-doctor-field"
          />
        </>
      )}
      <Box
        ref={actionButtonsRef}
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          mt: 2,
        }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={
            saving ||
            !hasValidLocation ||
            unusable.length > 0 ||
            // A doctor is confirmed for a time, so there has to be one; a
            // request for a doctor may still be open about when.
            (mode === "confirm" && requestedRanges.length === 0)
          }
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : mode === "confirm" ? (
            "Update & Confirm"
          ) : mode === "edit" ? (
            "Save Changes"
          ) : (
            "Create Assessment"
          )}
        </Button>
      </Box>
    </Box>
  );
}

// ============================================================================
// Interview Detail / Edit View
// ============================================================================

function InterviewDetailView({
  interview,
  onUpdated,
  onBack,
  onConfirmDetails,
  apiClient,
  renderSuccessToast = defaultRenderSuccessToast,
  renderErrorToast = defaultRenderErrorToast,
}: {
  interview: InterviewWithParticipants;
  onUpdated: () => void;
  onBack: () => void;
  /** Navigate to the "confirm details" form before actually confirming */
  onConfirmDetails: (participantId: string) => void;
  apiClient: InterviewManagementDialogApi;
  renderSuccessToast?: (props: { message: string }) => void;
  renderErrorToast?: (props: { message: string }) => void;
}) {
  const [participants, setParticipants] = useState<InterviewParticipant[]>(
    interview.participants,
  );
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const response = await apiClient.get(interview.id);
      setParticipants(response.data.participants);
    } catch {
      // keep current state on error
    }
  }, [apiClient, interview.id]);

  const handleConfirm = (participantId: string) => {
    onConfirmDetails(participantId);
  };

  const handleRevoke = async (participantId: string) => {
    setActionInProgress(participantId);
    try {
      await apiClient.revokeParticipant(interview.id, participantId);
      renderSuccessToast({ message: "Participant revoked" });
      await reload();
      onUpdated();
    } catch (error) {
      renderErrorToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleUnconfirm = async (participantId: string) => {
    setActionInProgress(participantId);
    try {
      await apiClient.unconfirmParticipant(interview.id, participantId);
      renderSuccessToast({ message: "Doctor unconfirmed" });
      await reload();
      onUpdated();
    } catch (error) {
      renderErrorToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCopyShareLink = (shareCode: string) => {
    const url = `${window.location.origin}/interview-invite/${shareCode}`;
    void navigator.clipboard.writeText(url);
    renderSuccessToast({ message: "Share link copied to clipboard" });
  };

  const isEditable = interview.status === "created";

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <Button onClick={onBack} size="small">
          &larr; Back to list
        </Button>
      </Box>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{
            fontWeight: "bold",
          }}
        >
          Assessment Details
        </Typography>
        {interview.address && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {interview.address}
            {interview.postalCode && `, ${interview.postalCode}`}
          </Typography>
        )}
        {interview.locationUnknown && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Location to be confirmed
          </Typography>
        )}
        {interview.timeRanges.length > 0 && (
          <Box
            sx={{
              mt: 0.5,
            }}
          >
            {interview.timeRanges.map((range, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {new Date(range.start).toLocaleString()} –{" "}
                {new Date(range.end).toLocaleString()}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: "bold",
        }}
      >
        Participants
      </Typography>
      {participants.length === 0 && (
        <Typography
          sx={{
            color: "text.secondary",
            py: 2,
          }}
        >
          No participants yet. Share the invite link with doctors.
        </Typography>
      )}
      {participants.map((participant) => (
        <Card
          key={participant.id}
          variant="outlined"
          sx={{ mb: 1 }}
          data-testid="participant-card"
        >
          <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                {getStatusIcon(participant.status)}
                <Typography variant="body2" noWrap>
                  {participant.userName || participant.userEmail || "Unknown"}
                </Typography>
                {(() => {
                  const chipLabel =
                    participant.status === "rejected" &&
                    participant.rejectionReason
                      ? `Not Available: ${participant.rejectionReason}`
                      : getStatusLabel(participant.status);

                  return (
                    <Tooltip title={chipLabel} arrow>
                      <Chip
                        label={chipLabel}
                        size="small"
                        color={getStatusColor(participant.status)}
                        variant="filled"
                        sx={{
                          maxWidth: 180,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: getStatusTextColor(participant.status),
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })()}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Tooltip title="Copy share link">
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleCopyShareLink(participant.shareCode);
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {isEditable &&
                  (participant.status === "pending" ||
                    participant.status === "accepted" ||
                    participant.status === "rejected") && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        handleConfirm(participant.id);
                      }}
                      disabled={actionInProgress === participant.id}
                    >
                      {actionInProgress === participant.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        "Confirm"
                      )}
                    </Button>
                  )}

                {isEditable &&
                  (participant.status === "accepted" ||
                    participant.status === "pending") && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        void handleRevoke(participant.id);
                      }}
                      disabled={actionInProgress === participant.id}
                    >
                      {actionInProgress === participant.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        "Revoke"
                      )}
                    </Button>
                  )}

                {isEditable &&
                  (participant.status === "confirmed" ||
                    participant.status === "pending-confirmed") && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        void handleUnconfirm(participant.id);
                      }}
                      disabled={actionInProgress === participant.id}
                    >
                      {actionInProgress === participant.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        "Unconfirm"
                      )}
                    </Button>
                  )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
